import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import IndieGameItem from '../components/IndieGameItem';
import IndieGameSkeleton from '../components/IndieGameSkeleton';

const IndieGames = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHashtag, setSelectedHashtag] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGames, setTotalGames] = useState(0);

  // Check URL params for hashtag on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashtagParam = urlParams.get('hashtag');
    if (hashtagParam) {
      setSelectedHashtag(hashtagParam);
    }
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedHashtag]);

  const fetchIndieGames = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 96,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedHashtag && { hashtag: selectedHashtag })
      });
      
      const response = await axios.get(`/api/indie-games?${params}`);
      setGames(response.data?.data || []);
      setTotalPages(response.data?.totalPages || 1);
      setTotalGames(response.data?.total || 0);
    } catch (err) {
      setError('Failed to load indie games');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedHashtag]);

  useEffect(() => {
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      fetchIndieGames();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [fetchIndieGames]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already triggered by useEffect when searchTerm changes
    // This just ensures Enter key works
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Show skeletons while loading
  if (loading) {
    return (
      <div className="container">
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <h2 style={{
            color: 'var(--text-accent)',
            fontSize: '32px',
            fontWeight: '800',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            Indie Games Showcase
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '16px',
            maxWidth: '600px',
            margin: '0 auto 24px',
            lineHeight: '1.6'
          }}>
            Discover amazing indie games created by passionate developers. Support independent creators and find your next favorite game.
          </p>
        </div>
        
        <div className="grid">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both` }}>
              <IndieGameSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        animation: 'fadeInUp 0.8s ease-out'
      }}>
        <h2 style={{
          color: 'var(--text-accent)',
          fontSize: '32px',
          fontWeight: '800',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          Indie Games Showcase
        </h2>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '16px',
          maxWidth: '600px',
          margin: '0 auto 24px',
          lineHeight: '1.6'
        }}>
          Discover amazing indie games created by passionate developers. Support independent creators and find your next favorite game.
        </p>

        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative', maxWidth: '400px', margin: '0 auto' }}>
            <input
              type="text"
              placeholder="Search indie games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 45px 12px 16px',
                borderRadius: '25px',
                border: '2px solid var(--border-color)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: '16px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--btn-primary)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            />
            <button
              type="submit"
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--btn-primary)';
                e.target.style.background = 'rgba(102, 126, 234, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--text-secondary)';
                e.target.style.background = 'none';
              }}
            >
              🔍
            </button>
          </div>
        </form>

        {/* Active hashtag filter */}
        {selectedHashtag && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
            color: 'white',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '16px'
          }}>
            <span>#{selectedHashtag}</span>
            <button
              onClick={() => {
                setSelectedHashtag('');
                setCurrentPage(1);
                window.history.pushState({}, '', '/indie-games');
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Results count */}
        {totalGames > 0 && (
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            Showing {games.length} of {totalGames} indie games
            {selectedHashtag && ` with #${selectedHashtag}`}
          </p>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {games.length === 0 ? (
        <div className="card" style={{
          textAlign: 'center',
          padding: '60px 20px',
          animation: 'fadeInUp 1s ease-out'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            opacity: 0.5
          }}>
            🎯
          </div>
          <h3 style={{
            color: 'var(--text-primary)',
            marginBottom: '12px',
            fontSize: '20px'
          }}>
            No Indie Games Yet
          </h3>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '16px',
            marginBottom: '24px'
          }}>
            Indie developers can showcase their games here. Be the first to share your creation!
          </p>
          <a
            href="/dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, var(--btn-primary), var(--btn-primary-hover))',
              color: 'var(--text-light)',
              textDecoration: 'none',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            Add Your Game
          </a>
        </div>
      ) : (
        <>
          <div className="grid" style={{ overflow: 'hidden' }}>
            {games.map((game, index) => (
              <React.Fragment key={game._id}>
                <div className="card-fade-in" style={{ 
                  animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both`,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>
                  <IndieGameItem game={game} />
                </div>
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {/* Developer Resources Section */}
      <div style={{
        marginTop: '40px',
        marginBottom: '40px',
        padding: '30px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '16px',
        border: '1px solid var(--border-color)',
        lineHeight: '1.7'
      }}>
        <h2 style={{
          color: 'var(--text-accent)',
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🚀 Tips for Developers
        </h2>
        
        <div style={{ color: 'var(--text)', fontSize: '15px' }}>
          <p style={{ marginBottom: '16px' }}>
            Some things I've learned that might help you get visibility here. Whether it's your first game or you've done this before, 
            here's what works:
          </p>

          <h3 style={{ 
            color: 'var(--primary)', 
            fontSize: '18px', 
            fontWeight: '600',
            marginTop: '20px',
            marginBottom: '12px' 
          }}>
            🎮 Getting Started
          </h3>
          
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <p style={{ marginBottom: '10px' }}>
              <strong>Set up:</strong> Change your account type to "Indie Developer" in settings. Unlocks game submission and analytics. 
              Link your Steam page or Itch.io if you want verification.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Submit your game:</strong> Go to your dashboard, click "Add Game." You need a good description (300+ words helps), 
              decent screenshots (at least 5), a trailer or gameplay video, store links, and relevant tags. First screenshot matters most, make 
              it count.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Make it good:</strong> Use a clear title. Pick tags that actually fit your game. Put your best screenshot first. 
              Keep the description interesting but concise. Add action items like "Wishlist now" with direct links.
            </p>
          </div>

          <h3 style={{ 
            color: 'var(--primary)', 
            fontSize: '18px', 
            fontWeight: '600',
            marginTop: '20px',
            marginBottom: '12px' 
          }}>
            📊 Getting Noticed
          </h3>
          
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <p style={{ marginBottom: '10px' }}>
              <strong>Giveaways work:</strong> Host key giveaways. Even small ones (5-10 keys) help. Builds wishlists, gets reviews, creates 
              buzz. Do one before launch, maybe another for updates.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Reply to reviews:</strong> Every review, good or bad. Thank people for positive ones. Fix issues in negative ones if 
              you can. Shows you care, builds trust.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Be around:</strong> Don't just spam your game. Comment on other devs' stuff, participate in discussions, share behind-the-scenes 
              content, help answer questions. Real engagement leads to cross-promotion and word-of-mouth.
            </p>
          </div>

          <h3 style={{ 
            color: 'var(--primary)', 
            fontSize: '18px', 
            fontWeight: '600',
            marginTop: '20px',
            marginBottom: '12px' 
          }}>
            💡 What I've Seen Work
          </h3>
          
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <p style={{ marginBottom: '10px' }}>
              <strong>Launch timing:</strong> List your game 2-3 months before launch. First giveaway 6-8 weeks out. Ramp up activity in 
              the final 2 weeks. Post on launch day. Early visibility compounds.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Share updates:</strong> People love dev logs. Post about milestones, problems you solved, design choices, art stuff. 
              Visuals (GIFs, screenshots) do better than text. Aim for 2-3 updates a week if you're actively developing.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Use hashtags:</strong> Mix broad ones (#IndieGame) with specific ones (#Roguelike). Make a unique one for your game. 
              Check trending tags and jump in when relevant.
            </p>
          </div>

          <div style={{
            marginTop: '20px',
            padding: '20px',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '12px',
            border: '2px solid var(--primary)'
          }}>
            <h4 style={{ 
              color: 'var(--primary)', 
              fontSize: '18px', 
              fontWeight: '600',
              marginBottom: '12px' 
            }}>
              🎯 Track Your Numbers
            </h4>
            <p style={{ marginBottom: '10px', fontSize: '14px' }}>
              Check your dashboard: <strong>Page views</strong> = people finding you; <strong>wishlist clicks</strong> = purchase intent; 
              <strong>reviews/ratings</strong> = social proof; <strong>giveaway entries</strong> = interest level; <strong>followers</strong> = 
              brand growth. If views are high but wishlists low, fix your description or screenshots.
            </p>
          </div>

          <div style={{
            marginTop: '20px',
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'var(--card-bg-secondary)',
            borderRadius: '12px'
          }}>
            <h4 style={{ color: 'var(--text-accent)', marginBottom: '12px' }}>Need Help?</h4>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
              Check the <a href="/blog" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>blog</a> for guides, 
              or see the <a href="/faq" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>FAQ</a> for common questions.
            </p>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginTop: '40px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              border: '2px solid var(--border-color)',
              background: currentPage === 1 ? 'var(--bg-secondary)' : 'var(--btn-primary)',
              color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text-light)',
              borderRadius: '8px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            ← Previous
          </button>

          {/* Page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={{
                  padding: '8px 12px',
                  border: '2px solid var(--border-color)',
                  background: currentPage === pageNum ? 'var(--btn-primary)' : 'var(--bg-card)',
                  color: currentPage === pageNum ? 'var(--text-light)' : 'var(--text-primary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  minWidth: '40px'
                }}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              border: '2px solid var(--border-color)',
              background: currentPage === totalPages ? 'var(--bg-secondary)' : 'var(--btn-primary)',
              color: currentPage === totalPages ? 'var(--text-secondary)' : 'var(--text-light)',
              borderRadius: '8px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              opacity: currentPage === totalPages ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default IndieGames;
